import React, { useState } from 'react';
import styled from '@emotion/styled';
import { FaTimes } from 'react-icons/fa';
import { getMockupUrl } from '../utils/fileHelpers';
import { sortSizes } from '../firebase/schema';
import { formatDate } from '../utils/dateFormatter';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-surface);
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  color: white;
  cursor: pointer;
  font-size: 20px;
  padding: 8px;
  border-radius: 4px;
  z-index: 10;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(var(--al-primary-rgb), 0.2);
    color: var(--al-primary);
  }
`;

const MockupSection = styled.div`
  width: 100%;
  background: var(--al-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  max-height: 400px;
  overflow: hidden;
  cursor: ${props => props.hasImage ? 'zoom-in' : 'default'};
`;

const MockupImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
`;

const NoMockup = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 1.2em;
  text-align: center;
  padding: 40px;
`;

const DetailsSection = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const DesignName = styled.h2`
  margin: 0;
  font-size: 1.4em;
  font-weight: 700;
  color: var(--al-primary);
  letter-spacing: 0.02em;
`;

const InvoiceNumber = styled.div`
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
`;

const QuantityBadge = styled.div`
  background: var(--al-primary);
  color: var(--al-bg);
  padding: 12px 20px;
  border-radius: 4px;
  font-size: 1.5em;
  font-weight: 700;
  text-align: center;
  min-width: 80px;
`;

const QuantityLabel = styled.div`
  font-size: 0.5em;
  font-weight: 600;
  margin-bottom: 2px;
  opacity: 0.8;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  padding: 16px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const InfoLabel = styled.div`
  font-size: 0.7em;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.div`
  font-size: 1em;
  font-weight: 600;
  color: white;
`;

const InkSection = styled.div`
  margin-top: 20px;
`;

const InkSectionTitle = styled.h3`
  font-size: 0.85em;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 12px 0;
  letter-spacing: 0.05em;
`;

const InkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InkCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: 12px;
  border-radius: 4px;
  border-left: 3px solid ${props => props.color || 'var(--al-primary)'};
`;

const InkLocation = styled.div`
  font-size: 0.7em;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
`;

const InkColor = styled.div`
  font-size: 0.85em;
  color: white;
  margin: 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: var(--al-primary);
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

const NoColors = styled.div`
  font-size: 0.8em;
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
`;

const InkNotesCard = styled.div`
  background: linear-gradient(135deg, rgba(var(--al-primary-rgb), 0.15) 0%, rgba(var(--al-primary-rgb), 0.05) 100%);
  border: 2px solid var(--al-primary);
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  position: relative;
  
  &::before {
    content: '📝';
    position: absolute;
    top: -12px;
    left: 16px;
    background: var(--al-surface);
    padding: 0 8px;
    font-size: 1.2em;
  }
`;

const InkNotesFlag = styled.div`
  position: absolute;
  top: -12px;
  right: 16px;
  background: var(--al-primary);
  color: var(--al-bg);
  padding: 4px 12px;
  font-size: 0.65em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 2px;
`;

const InkNotesTitle = styled.h4`
  margin: 0 0 12px 0;
  color: var(--al-primary);
  font-size: 0.85em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InkNotesContent = styled.div`
  color: white;
  font-size: 0.9em;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const SizesSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const SizesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const SizeChip = styled.div`
  background: ${props => props.hasQty ? 'rgba(var(--al-primary-rgb), 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.hasQty ? 'rgba(var(--al-primary-rgb), 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  padding: 8px 12px;
  border-radius: 4px;
  text-align: center;
  min-width: 50px;
`;

const SizeLabel = styled.div`
  font-size: 0.7em;
  color: rgba(255, 255, 255, 0.5);
`;

const SizeQty = styled.div`
  font-size: 1.1em;
  font-weight: 600;
  color: ${props => props.hasQty ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.3)'};
`;

const LargeImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  cursor: zoom-out;
`;

const LargeImage = styled.img`
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
`;

const DueDateBadge = styled.div`
  font-size: 0.75em;
  color: ${props => props.isOverdue ? '#FF6B6B' : 'rgba(255, 255, 255, 0.6)'};
  margin-top: 4px;
  ${props => props.isOverdue && `font-weight: 600;`}
`;

const PrintLineModal = ({ line, orderNumber, dueDate, onClose }) => {
  const [showLargeImage, setShowLargeImage] = useState(false);
  
  if (!line) return null;
  
  // Try multiple sources for mockup URL
  const mockupUrl = getMockupUrl(line) || 
    line.artworkUrl || 
    line.mockupUrl || 
    line.designFileUrl ||
    line.files?.mockup?.url ||
    line.dtgArtworkUrl;
    
  const totalQuantity = line.totalQuantity || 
    Object.values(line.sizes || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  
  // Get print type
  const printType = line.type || 'N/A';
  
  // Check if overdue
  const isOverdue = dueDate && new Date(dueDate) < new Date();
  
  // Get sizes sorted
  const sizes = line.sizes || {};
  const sortedSizes = Object.entries(sizes)
    .sort(([a], [b]) => sortSizes(a, b));

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
          
          {/* Mockup Image */}
          <MockupSection 
            hasImage={!!mockupUrl}
            onClick={() => mockupUrl && setShowLargeImage(true)}
          >
            {mockupUrl ? (
              <MockupImage src={mockupUrl} alt={line.designName} />
            ) : (
              <NoMockup>No Mockup Available</NoMockup>
            )}
          </MockupSection>
          
          <DetailsSection>
            {/* Header with Design Name and Quantity */}
            <Header>
              <div>
                <DesignName>{line.designName || line.design || 'Unnamed Design'}</DesignName>
                <InvoiceNumber>Invoice #{orderNumber || 'N/A'}</InvoiceNumber>
                {dueDate && (
                  <DueDateBadge isOverdue={isOverdue}>
                    Due: {formatDate(dueDate)} {isOverdue && '(OVERDUE)'}
                  </DueDateBadge>
                )}
                {line.inkNotes && (
                  <div style={{
                    marginTop: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'linear-gradient(90deg, rgba(var(--al-primary-rgb), 0.2), rgba(var(--al-primary-rgb), 0.1))',
                    border: '1px solid var(--al-primary)',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '0.75em',
                    color: 'var(--al-primary)',
                    fontWeight: '600'
                  }}>
                    📝 HAS SPECIAL INK INSTRUCTIONS - SEE BELOW
                  </div>
                )}
              </div>
              <QuantityBadge>
                <QuantityLabel>Quantity</QuantityLabel>
                {totalQuantity}
              </QuantityBadge>
            </Header>
            
            {/* Garment Info */}
            <InfoGrid>
              <InfoCard>
                <InfoLabel>Garment Style</InfoLabel>
                <InfoValue>{line.garmentId || line.style || 'N/A'}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Garment Color</InfoLabel>
                <InfoValue>{line.garmentColor || line.color || 'N/A'}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Print Type</InfoLabel>
                <InfoValue>{printType.toUpperCase()}</InfoValue>
              </InfoCard>
            </InfoGrid>
            
            {/* Ink Colors */}
            <InkSection>
              <InkSectionTitle>Ink Colors</InkSectionTitle>
              <InkGrid>
                <InkCard color="#4CAF50">
                  <InkLocation>Front ({line.frontInkColors?.length || 0})</InkLocation>
                  {line.frontInkColors?.length > 0 ? (
                    line.frontInkColors.map((color, i) => (
                      <InkColor key={i}>{color}</InkColor>
                    ))
                  ) : (
                    <NoColors>None</NoColors>
                  )}
                </InkCard>
                <InkCard color="#2196F3">
                  <InkLocation>Back ({line.backInkColors?.length || 0})</InkLocation>
                  {line.backInkColors?.length > 0 ? (
                    line.backInkColors.map((color, i) => (
                      <InkColor key={i}>{color}</InkColor>
                    ))
                  ) : (
                    <NoColors>None</NoColors>
                  )}
                </InkCard>
                <InkCard color="#FF9800">
                  <InkLocation>Other ({line.otherInkColors?.length || 0})</InkLocation>
                  {line.otherInkColors?.length > 0 ? (
                    line.otherInkColors.map((color, i) => (
                      <InkColor key={i}>{color}</InkColor>
                    ))
                  ) : (
                    <NoColors>None</NoColors>
                  )}
                </InkCard>
              </InkGrid>
              
              {/* Ink Notes - Special Instructions */}
              {line.inkNotes && (
                <InkNotesCard>
                  <InkNotesFlag>Special Instructions</InkNotesFlag>
                  <InkNotesTitle>Ink Notes from Manager</InkNotesTitle>
                  <InkNotesContent>{line.inkNotes}</InkNotesContent>
                </InkNotesCard>
              )}
            </InkSection>
            
            {/* Size Breakdown */}
            {sortedSizes.length > 0 && (
              <SizesSection>
                <InkSectionTitle>Size Breakdown</InkSectionTitle>
                <SizesGrid>
                  {sortedSizes.map(([size, qty]) => (
                    <SizeChip key={size} hasQty={qty > 0}>
                      <SizeLabel>{size}</SizeLabel>
                      <SizeQty hasQty={qty > 0}>{qty || 0}</SizeQty>
                    </SizeChip>
                  ))}
                </SizesGrid>
              </SizesSection>
            )}
          </DetailsSection>
        </ModalContent>
      </ModalOverlay>
      
      {/* Large Image Viewer */}
      {showLargeImage && mockupUrl && (
        <LargeImageOverlay onClick={() => setShowLargeImage(false)}>
          <LargeImage src={mockupUrl} alt={line.designName} />
        </LargeImageOverlay>
      )}
    </>
  );
};

export default PrintLineModal;

